import DrawSVGPlugin from 'gsap/dist/DrawSVGPlugin';
import { gsap } from 'gsap';
import type { RefObject } from 'react';
import { findTransitionController } from '../../../../util/core-transition-component';

gsap.registerPlugin(DrawSVGPlugin);

export const TRANSITION_IN_DURATION = 0.6;

interface LogoIntroSectionRefs {
  tlRef?: RefObject<gsap.core.Timeline | undefined>;
  elementRef?: RefObject<HTMLDivElement | null>;
  logoTextRef?: RefObject<HTMLHeadingElement | null>;
  kanjiTextRef?: RefObject<HTMLParagraphElement | null>;

  leftLogoRef?: RefObject<SVGPathElement | null>;
  rightLogoRef?: RefObject<SVGPathElement | null>;
}

export function setupTransitionInTimeline(
  timeline: gsap.core.Timeline,
  { tlRef, elementRef }: LogoIntroSectionRefs,
): void {
  const nestedTimeline = tlRef?.current;
  const element = elementRef?.current;

  if (!nestedTimeline || !element) {
    return;
  }

  timeline.pause(0);
  timeline.progress(0);

  timeline.fromTo(
    nestedTimeline,
    { progress: 0 },
    {
      duration: 1.35,
      ease: 'none',
      progress: 0.6,
      overwrite: 'auto',
    },
  );

  timeline.to(
    element,
    {
      opacity: 1,
      duration: 0.05,
    },
    '<',
  );
}

function transitionIn(
  timeline: gsap.core.Timeline,
  { logoTextRef, kanjiTextRef, leftLogoRef, rightLogoRef }: LogoIntroSectionRefs,
): void {
  const logoTextController = findTransitionController(logoTextRef);
  const kanjiTextController = findTransitionController(kanjiTextRef);

  if (logoTextController) {
    timeline.add(logoTextController.getTimeline('in') as gsap.core.Timeline, '');
  }

  if (kanjiTextController) {
    timeline.add(kanjiTextController.getTimeline('in') as gsap.core.Timeline, '>-0.3');
  }

  if (leftLogoRef?.current && rightLogoRef?.current) {
    const duration = 1.1;

    timeline.fromTo(
      leftLogoRef?.current,
      {
        drawSVG: '0% 0%',
      },
      {
        drawSVG: '0% -100%',
        ease: 'easeInOut',
        duration,
      },
      '<',
    );
    timeline.fromTo(
      rightLogoRef?.current,
      {
        drawSVG: '0% 0%',
      },
      {
        drawSVG: '0% -100%',
        ease: 'easeInOut',
        duration,
      },
      '<',
    );
  }

  timeline.addLabel('transitionInComplete');
}

function transitionOut(
  timeline: gsap.core.Timeline,
  { logoTextRef, kanjiTextRef, leftLogoRef, rightLogoRef }: LogoIntroSectionRefs,
): void {
  const logoTextController = findTransitionController(logoTextRef);
  const kanjiTextController = findTransitionController(kanjiTextRef);

  if (leftLogoRef?.current && rightLogoRef?.current) {
    const duration = 1;

    timeline.fromTo(
      leftLogoRef?.current,
      {
        drawSVG: '0% 100%',
      },
      {
        drawSVG: '0% 0%',
        ease: 'Quad.easeInOut',
        duration,
      },
      '>',
    );
    timeline.fromTo(
      rightLogoRef?.current,
      {
        drawSVG: '0% 100%',
      },
      {
        drawSVG: '0% 0%',
        ease: 'Quad.easeInOut',
        duration,
      },
      '<',
    );
  }

  if (logoTextController) {
    timeline.add(logoTextController.getTimeline('out') as gsap.core.Timeline, '<20%');
  }

  if (kanjiTextController) {
    timeline.add(kanjiTextController.getTimeline('out') as gsap.core.Timeline, '>-0.3');
  }
}

export function timelineInOut(
  timeline: gsap.core.Timeline,
  { logoTextRef, kanjiTextRef, leftLogoRef, rightLogoRef }: LogoIntroSectionRefs,
): void {
  transitionIn(timeline, { logoTextRef, kanjiTextRef, leftLogoRef, rightLogoRef });
  transitionOut(timeline, { logoTextRef, kanjiTextRef, leftLogoRef, rightLogoRef });
}
